import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  Button,
} from '@mui/material';
import { loadDraft, saveDraft, clearDraft } from '../../utils/draftStorage';

// Autosave the in-progress text shortly after the user stops typing. This is NOT
// per-keystroke — localStorage is written at most once per ~second of continuous
// typing, and instantly after a pause. Kept short so the draft stays fresh.
const AUTOSAVE_DEBOUNCE_MS = 1000;

/**
 * Edit dialog for the work-order fault description (vikakuvaus).
 *
 * Lives in its own component so that typing only re-renders this small dialog
 * rather than the large MaintenanceProcessView — which previously re-rendered on
 * every keystroke and caused noticeable input lag.
 *
 * In-progress text is autosaved to localStorage (failsafe, debounced) so it is
 * never lost on a misclicked Cancel, a connection error, or a tab close.
 */
function DescriptionEditDialog({
  open,
  onClose,
  draftKey,
  initialValue,
  initialShowToCustomer,
  onSave,
  onDraftChange,
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [showToCustomer, setShowToCustomer] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  const notifyDraft = (hasDraft) => {
    if (onDraftChange) {
      onDraftChange(hasDraft);
    }
  };

  // Initialise (and restore any draft) each time the dialog opens.
  useEffect(() => {
    if (!open) {
      return;
    }
    const savedValue = initialValue || '';
    const draft = loadDraft(draftKey);
    if (draft && (draft.description || '') !== savedValue) {
      setValue(draft.description || '');
      setShowToCustomer(!!draft.show_description_to_customer);
      setDraftRestored(true);
    } else {
      setValue(savedValue);
      setShowToCustomer(!!initialShowToCustomer);
      setDraftRestored(false);
    }
    setSaveError(false);
    // Only re-run when the dialog opens — initial values are a snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced autosave while the dialog is open.
  useEffect(() => {
    if (!open || !draftKey) {
      return undefined;
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      const savedValue = initialValue || '';
      const savedShow = !!initialShowToCustomer;
      if (value === savedValue && showToCustomer === savedShow) {
        clearDraft(draftKey);
        notifyDraft(false);
      } else {
        saveDraft(draftKey, { description: value, show_description_to_customer: showToCustomer });
        notifyDraft(true);
      }
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value, showToCustomer, draftKey, initialValue, initialShowToCustomer]);

  // Persist the latest text immediately (used on close so quick Cancel never
  // loses keystrokes typed within the debounce window).
  const flushDraft = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (!draftKey) {
      return;
    }
    const savedValue = initialValue || '';
    const savedShow = !!initialShowToCustomer;
    if (value === savedValue && showToCustomer === savedShow) {
      clearDraft(draftKey);
      notifyDraft(false);
    } else {
      saveDraft(draftKey, { description: value, show_description_to_customer: showToCustomer });
      notifyDraft(true);
    }
  };

  const handleClose = () => {
    flushDraft();
    onClose();
  };

  const handleDiscardDraft = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    clearDraft(draftKey);
    setValue(initialValue || '');
    setShowToCustomer(!!initialShowToCustomer);
    setDraftRestored(false);
    notifyDraft(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ description: value, show_description_to_customer: showToCustomer });
      // Saved to the server — the draft is no longer needed.
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      clearDraft(draftKey);
      notifyDraft(false);
      setSaveError(false);
      onClose();
    } catch (err) {
      // Keep the dialog open and the text intact; persist immediately so nothing
      // is lost, and show a reassuring (non-technical) message.
      saveDraft(draftKey, { description: value, show_description_to_customer: showToCustomer });
      notifyDraft(true);
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      // The field grows as the mechanic types; without a height cap the Save
      // button gets pushed off a phone screen. Let DialogContent scroll instead.
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle>{t('Edit problem description')}</DialogTitle>
      <DialogContent>
        {draftRestored && (
          <Alert
            severity="info"
            sx={{ mb: 1.5, borderRadius: '8px' }}
            action={
              <Button color="inherit" size="small" onClick={handleDiscardDraft}>
                {t('Discard draft')}
              </Button>
            }
          >
            {t('Restored unsaved draft')}
          </Alert>
        )}
        {saveError && (
          <Alert severity="error" sx={{ mb: 1.5, borderRadius: '8px' }}>
            {t('Saving failed. Your text was kept as a draft — you can try again.')}
          </Alert>
        )}
        <TextField
          autoFocus
          margin="dense"
          label={t('Problem description')}
          fullWidth
          multiline
          minRows={6}
          maxRows={18}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (saveError) {
              setSaveError(false);
            }
          }}
          placeholder={t('Describe the problem or reason for the maintenance...')}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={showToCustomer}
              onChange={(e) => setShowToCustomer(e.target.checked)}
            />
          }
          label={t('Show to customer on invoice, quote, cost estimate and tracking page')}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          {t('Cancel')}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DescriptionEditDialog;
